import { Boxes, Bug, Cloud, Container, Copy, ListTree } from 'lucide-react';
import { useMemo } from 'react';
import { toast } from 'sonner';
import aws_dark_icon from '@root/assets/img/cloud_provider_icons/aws_dark_icon.png';
import aws_icon from '@root/assets/img/cloud_provider_icons/aws_icon.png';
import gcp_icon from '@root/assets/img/header/cloud_providers/gcp.png';
import ClustersIcon from '@root/assets/svg/Dashboard/ClustersIcon';
import WorkloadIcon from '@root/assets/svg/Dashboard/WorkloadIcon';
import SeverityIcons from '@root/components/SeverityIcons/SeverityIcons';
import { azure_icon } from '@root/components/utilities/CloudIconsUtil';
import Tooltip from '@root/components/v2/ui-elements/Tooltip';
import { useTheme } from '@root/context/ThemeContext';
import { useEnvironments } from '@root/hooks/environments';
import { capitalizeFirstLetter } from '@root/utilities/string';
import './K8sOverlayHeaderIdentity.scss';

// Left side of the Kubernetes vulnerabilities overlay header, laid out like the inventory resource overlay's: an
// icon, the title and a status badge over a quieter row of what the clicked row carries (the subject outside the
// CVE overlay, its cluster and its environment IDs), each led by its own icon. Items the row lacks are left out.
// The Findings overlay (`type="findings"`) is titled by its workload, so its subject is the CVE.

const CLOUD_ICONS = {
  aws: { src: aws_icon, darkSrc: aws_dark_icon, label: 'AWS' },
  azure: { src: azure_icon, label: 'Azure' },
  gcp: { src: gcp_icon, label: 'GCP' },
};

// What the image, workload and SBOM overlays are about (their title is the cluster): the row field naming it and
// its icon. The CVE overlay's title is the CVE ID itself, so it has no subject item.
// `renderIcon` replaces the icon and its label tooltip when the icon carries its own (the severity icon's).
const SUBJECTS = {
  images: { label: 'Image', field: 'name', icon: Container },
  workloads: { label: 'Workload', field: 'workload', icon: Boxes },
  sbom: { label: 'Package', field: 'name', icon: ListTree },
  findings: {
    label: 'CVE',
    field: 'cve_id',
    renderIcon: row => <SeverityIcons severity={row?.severity} height="14px" width="14px" />,
  },
};

// Icon tile before the title, for the overlays whose title is not the cluster: the CVE overlay's bug in the CVEs
// table's colours, the Findings overlay's workload icon in the Findings table's Workload column colours.
const IDENTITY_ICONS = {
  cves: { icon: <Bug size={20} /> },
  findings: { icon: <WorkloadIcon width="20px" height="20px" />, background: 'rgba(147, 51, 234, 0.12)' },
};

// Outside these the title is already the cluster.
const SHOWS_CLUSTER = ['cves', 'findings'];

const copyToClipboard = async (event, text) => {
  event.stopPropagation();
  await navigator.clipboard?.writeText(text);
  toast.info('Copied to clipboard!');
};

const MetaItem = ({ icon, tooltip, value, copyLabel }) => (
  <span className="K8sOverlay__MetaItem">
    <Tooltip title={tooltip} isIcon={true}>
      <span className="K8sOverlay__MetaIcon">{icon}</span>
    </Tooltip>
    <Tooltip title={value}>
      <span className="K8sOverlay__MetaText">{value}</span>
    </Tooltip>
    {copyLabel && (
      <Tooltip title={copyLabel} isIcon={true}>
        <button
          type="button"
          className="K8sOverlay__CopyButton"
          aria-label={copyLabel}
          onClick={event => copyToClipboard(event, value)}>
          <Copy size={12} />
        </button>
      </Tooltip>
    )}
  </span>
);

const unique = values => [...new Set(values.filter(Boolean))];

// A CVE list row lists every cluster it is in as { cluster, account_id }; an inventory row names its one cluster
// and environment.
const clusterNames = row =>
  Array.isArray(row?.clusters) ? unique(row.clusters.map(item => item?.cluster ?? item)).join(', ') : row?.cluster;

const environmentIds = row =>
  unique([...(Array.isArray(row?.clusters) ? row.clusters.map(item => item?.account_id) : []), row?.account_id]);

const K8sOverlayHeaderIdentity = ({ type, selectedRow, title }) => {
  const { isDarkMode } = useTheme();
  const environments = useEnvironments();
  const cloudByEnvironment = useMemo(
    () => Object.fromEntries(environments.map(env => [env.id, env.cloudProvider?.toLowerCase()])),
    [environments],
  );

  const subject = SUBJECTS[type];
  const subjectValue = subject && selectedRow?.[subject.field];
  const identityIcon = IDENTITY_ICONS[type];
  const status = selectedRow?.status;
  const cluster = SHOWS_CLUSTER.includes(type) ? clusterNames(selectedRow) : null;

  const metaItems = [
    subjectValue && (
      <MetaItem
        key="subject"
        icon={subject.renderIcon ? subject.renderIcon(selectedRow) : <subject.icon size={14} />}
        tooltip={subject.renderIcon ? null : subject.label}
        value={subjectValue}
        copyLabel={`Copy ${subject.label}`}
      />
    ),
    cluster && (
      <MetaItem key="cluster" icon={<ClustersIcon width="14px" height="14px" />} tooltip="Cluster" value={cluster} />
    ),
    // Each environment ID led by its own cloud's logo: the row's cloud_provider, else the environment's.
    ...environmentIds(selectedRow).map(id => {
      const cloud = CLOUD_ICONS[cloudByEnvironment[id] ?? selectedRow?.cloud_provider?.toLowerCase()];
      return (
        <MetaItem
          key={`environment-${id}`}
          icon={
            cloud ? (
              <img
                src={(isDarkMode && cloud.darkSrc) || cloud.src}
                alt={cloud.label}
                className="K8sOverlay__CloudIcon"
              />
            ) : (
              <Cloud size={14} />
            )
          }
          tooltip={cloud ? `${cloud.label} environment` : 'Environment'}
          value={id}
        />
      );
    }),
  ].filter(Boolean);

  return (
    <div className="K8sOverlay__Identity">
      {identityIcon && (
        <span
          className="K8sOverlay__IdentityIcon"
          aria-hidden="true"
          style={identityIcon.background ? { backgroundColor: identityIcon.background } : undefined}>
          {identityIcon.icon}
        </span>
      )}
      <div className="K8sOverlay__TitleBlock">
        <div className="K8sOverlay__TitleRow">
          <Tooltip title={title} placement="bottom">
            <span className="K8sOverlay__Title">{title}</span>
          </Tooltip>
          {status && (
            <span
              className={`K8sOverlay__Status K8sOverlay__Status--${status.toLowerCase() === 'active' ? 'active' : 'neutral'}`}>
              {capitalizeFirstLetter(status)}
            </span>
          )}
        </div>
        {metaItems.length > 0 && (
          <div className="K8sOverlay__Meta">
            {metaItems.flatMap((item, index) =>
              index === 0
                ? [item]
                : [
                    <span key={`separator-${index}`} className="K8sOverlay__MetaSeparator" aria-hidden="true">
                      /
                    </span>,
                    item,
                  ],
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default K8sOverlayHeaderIdentity;
